import { useMemo } from "react";
import "./CompanyEmissionReportPage.scss";
import CustomTable from "../../../common/customTable/CustomTable";
import { AGGREGATION_DESCRIPTIONS } from "./AggregationConstnats";

const CompanyEmissionReportPage = ({shipERs, companyParticulars, annualEmissions,isLoading }) => {	
	// Company Particulars Columns
	
	const companyParticularsColumns = useMemo(
		() => [
			{
				accessorKey: "companyImoNumber",
				header: "Company IMO Number",
				size: 150,
				muiTableHeadCellProps: { align: "center" },
				muiTableBodyCellProps: ({ row }) => ({
					align: "center",
                    children: <span>{row.original.companyImoNumber}</span>,
                }),
			},
			{
				accessorKey: "xmlCompanyName",
				header: "Company Name",
				size: 200,
				muiTableHeadCellProps: { align: "center" },
    			muiTableBodyCellProps: { align: "center" },
			},
		],
		[]
	);

	//partial date formatter
	 const formatPartialDate = (dateStr) => {
			if (!dateStr) return "";
			const [day, month] = dateStr.split("-");
			return `${day}/${month}`;
		};
	// Ship ERs Columns
	const shipERsColumns = useMemo(
		() => [
			{
				accessorKey: "shipImo",
				header: "Ship IMO",
				size: 100,
				muiTableHeadCellProps: { align: "center" },
				muiTableBodyCellProps: ({ row }) => ({
					align: "center",
					children: <span>{row.original.shipImo}</span>,
				}),
			},
			{
				accessorKey: "name",
				header: "Name",
				size: 120,
				muiTableHeadCellProps: { align: "center" },
    			muiTableBodyCellProps: { align: "center" },
			},
			{
				accessorKey: "flag",
				header: "Flag",
				size: 100,
				muiTableHeadCellProps: { align: "center" },
    			muiTableBodyCellProps: { align: "center" },
			},
			{
				accessorKey: "shipType",
				header: "Ship Type",
				size: 120,
				muiTableHeadCellProps: { align: "center" },
    			muiTableBodyCellProps: { align: "center" },
			},
			{
				accessorKey: "grossTonnage",
				header: "Gross Tonnage",
				size: 120,
				muiTableHeadCellProps: { align: "center" },
				muiTableBodyCellProps: ({ row }) => ({
					align: "center",
					children: <span>{row.original.grossTonnage}</span>,
				}),
			},
			{
				accessorKey: "iceClass",
				header: "Ice Class",
				size: 100,
				muiTableHeadCellProps: { align: "center" },
    			muiTableBodyCellProps: { align: "center" },
			},
			{
				accessorKey: "reportingPeriod",
				header: "Reporting Period",
				size: 150,
				muiTableHeadCellProps: { align: "center" },
				muiTableBodyCellProps: ({ row }) => {
					const { reportingPeriod, partialPeriodFromDate, partialPeriodToDate } = row.original;

					let displayValue = reportingPeriod;

					if (partialPeriodFromDate && partialPeriodToDate) {
						displayValue = `${reportingPeriod} (from ${formatPartialDate(partialPeriodFromDate)} to ${formatPartialDate(partialPeriodToDate)})`;
					}

					return {
						align: "center",
						children: <span>{displayValue}</span>,
					};
				},
			},
			{
				accessorKey: "shipownerImoNumber",
				header: "Shipowner IMO Number",
				size: 150,
				muiTableHeadCellProps: { align: "center" },
				muiTableBodyCellProps: ({ row }) => ({
					align: "center",
					children: <span>{row.original.shipownerImoNumber}</span>,
				}),
			},
			{
				accessorKey: "co2eqEmissions",
				header: "CO2eq Emissions [m tonnes]",
				size: 150,
				muiTableHeadCellProps: { align: "center" },
				muiTableBodyCellProps: ({ row }) => ({
					align: "center",
					children: <span>{row.original.co2eqEmissions}</span>,
				}),
			},

			{
				accessorKey: "erStatus",
				header: "ER Status",
				size: 150,
				muiTableHeadCellProps: { align: "center" },
    			muiTableBodyCellProps: { align: "center" },
			},
		],
		[]
	);

	// Annual Emissions - Totals Columns
	const annualEmissionsColumns = useMemo(
		() => [
			{
				accessorKey: "aggregationDescription",
				accessorFn: (row) => {
					const value = row.aggregationDescription;

					const index = Number(value?.replace("Point ", "")) - 1;

					return AGGREGATION_DESCRIPTIONS[index] || value;
				},
				header: (
					<>
						Aggregation as determined according to Part C,
						<br />
						Annex II to Regulation (EU) 2015/757
					</>
				),
				size: 400,
			},
			{
				accessorKey: "co2",
				header: (
					<div style={{ textAlign: "center" }}>
						CO2 <br /> [m tonnes]
					</div>
				),
				size: 120,
				muiTableHeadCellProps: { align: "center" },
				muiTableBodyCellProps: ({ row }) => ({
                    align: "center",
                    children: <span>{row.original.co2}</span>,
                }),
			},
			{
				id: "ch4",
				header: (
					<div style={{ textAlign: "center" }}>
						CH4
						<br />
						[m tonnes]
						<br />
						[m tonnes CO2eq]
					</div>
				),
				size: 150,
				muiTableHeadCellProps: { align: "center" },
				muiTableBodyCellProps: { align: "center" },
				Cell: ({ row }) => (
					<div style={{ textAlign: "center" }}>
						<div>{Number(row.original.ch4Tonnes || 0)}</div>
						<div>{Number(row.original.ch4Co2EqTonnes || 0)}</div>
					</div>
				),
			},
			{
				id: "n2o",
				header: (
					<div style={{ textAlign: "center" }}>
						N2O
						<br />
						[m tonnes]
						<br />
						[m tonnes CO2eq]
					</div>
				),
				size: 150,
				muiTableHeadCellProps: { align: "center" },
				muiTableBodyCellProps: { align: "center" },
				Cell: ({ row }) => (
					<div style={{ textAlign: "center" }}>
						<div>{Number(row.original.n2OTonnes || 0)}</div>
						<div>{Number(row.original.n2OCo2EqTonnes || 0)}</div>
					</div>
				),
			},
			{
				accessorKey: "totalGhgEmissions",
				header: (
					<div style={{ textAlign: "center" }}>
						Total GHG Emissions <br /> [m tonnes CO2eq]
					</div>
				),
				size: 180,
				muiTableHeadCellProps: { align: "center" },
				muiTableBodyCellProps: { align: "center" },
				muiTableBodyCellProps: ({ row }) => ({
                    align: "center",
                    children: <span>{row.original.totalGhgEmissions}</span>,
                }),
			},
		],
		[]
	);

	return (
		<div className="company-emission-report-page">
			{/* Company Particulars Section */}
			<div className="section-container">
				<CustomTable
					columnsData={companyParticularsColumns}
					renderTableData={companyParticulars || []}
					customProps={{
						header: "Company Particulars",
						enableSort: false,
						tableName: "company-particulars",
						maxHeight: "30vh",
						isLoading: isLoading,
					}}
				/>
			</div>

			{/* Ship ERs Section */}
			<div className="section-container">
				<CustomTable
					columnsData={shipERsColumns}
					renderTableData={shipERs || []}
					customProps={{
						header: "Ship ERs",
						enableSort: false,
						tableName: "ship-ers",
						maxHeight: "30vh",
						isLoading: isLoading,
					}}
				/>
			</div>

			{/* Annual Emissions - Totals Section */}
			<div className="section-container">
				<CustomTable
					columnsData={annualEmissionsColumns}
					renderTableData={annualEmissions || []}
					customProps={{
						header: "Annual Emissions - Totals",
						enableSort: false,
						tableName: "annual-emissions-totals",
						maxHeight: "30vh",
						isLoading: isLoading,
					}}
				/>
			</div>
		</div>
	);
};

export default CompanyEmissionReportPage;
